import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Chip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

import tenantsContext from '../../context/tenants/tenantsContext';
import Routes from '../../utils/Routes';

function Tenants() {
    const navigate = useNavigate();
    const tContext = useContext(tenantsContext);
    const { tenants, getAllTenants, addTenant, updateTenant, deleteTenant } = tContext;

    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentTenant, setCurrentTenant] = useState({
        codTenant: '',
        name: '',
        urlBackend: '',
        codVersion: '',
        codBuild: '',
        enabled: true
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            await getAllTenants();
            setLoading(false);
        };
        fetchData();
    }, []);

    const handleOpenDialog = (tenant = null) => {
        if (tenant) {
            setEditMode(true);
            setCurrentTenant({ ...tenant });
        } else {
            setEditMode(false);
            setCurrentTenant({
                codTenant: '',
                name: '',
                urlBackend: '',
                codVersion: '',
                codBuild: '',
                enabled: true
            });
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentTenant({
            codTenant: '',
            name: '',
            urlBackend: '',
            codVersion: '',
            codBuild: '',
            enabled: true
        });
    };

    const handleSave = async () => {
        try {
            if (editMode) {
                await updateTenant(currentTenant);
                Swal.fire('Exito', 'Tenant actualizado correctamente', 'success');
            } else {
                await addTenant(currentTenant);
                Swal.fire('Exito', 'Tenant creado correctamente', 'success');
            }
            handleCloseDialog();
            await getAllTenants();
        } catch (error) {
            Swal.fire('Error', error.message || 'Error al guardar tenant', 'error');
        }
    };

    const handleDelete = async (tenant) => {
        const result = await Swal.fire({
            title: 'Confirmar eliminacion',
            text: `Esta seguro de eliminar el tenant ${tenant.codTenant}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Si, eliminar',
            cancelButtonText: 'Cancelar'
        });

        if (result.isConfirmed) {
            try {
                await deleteTenant(tenant);
                Swal.fire('Eliminado', 'Tenant eliminado correctamente', 'success');
                await getAllTenants();
            } catch (error) {
                Swal.fire('Error', error.message || 'Error al eliminar tenant', 'error');
            }
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('es-AR');
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate(Routes.abms)} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h4" sx={{ color: '#3d4d87', fontWeight: 'bold', flex: 1 }}>
                    Tenants
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                    sx={{ backgroundColor: '#3d4d87' }}
                >
                    Nuevo Tenant
                </Button>
            </Box>

            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
                        <TableRow>
                            <TableCell>Codigo</TableCell>
                            <TableCell>Nombre</TableCell>
                            <TableCell>URL Backend</TableCell>
                            <TableCell>Version</TableCell>
                            <TableCell>Build</TableCell>
                            <TableCell>Estado</TableCell>
                            <TableCell align="center">Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {tenants?.map((tenant) => (
                            <TableRow key={tenant._id} hover>
                                <TableCell>{tenant.codTenant}</TableCell>
                                <TableCell>{tenant.name}</TableCell>
                                <TableCell>{tenant.urlBackend || '-'}</TableCell>
                                <TableCell>{tenant.codVersion || '-'}</TableCell>
                                <TableCell>{tenant.codBuild || '-'}</TableCell>
                                <TableCell>
                                    <Chip
                                        size="small"
                                        label={tenant.enabled ? 'Activo' : 'Inactivo'}
                                        color={tenant.enabled ? 'success' : 'error'}
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <IconButton onClick={() => handleOpenDialog(tenant)} color="primary">
                                        <EditIcon />
                                    </IconButton>
                                    <IconButton onClick={() => handleDelete(tenant)} color="error">
                                        <DeleteIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle>{editMode ? 'Editar Tenant' : 'Nuevo Tenant'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Codigo del Tenant"
                            value={currentTenant.codTenant}
                            onChange={(e) => setCurrentTenant({ ...currentTenant, codTenant: e.target.value.toUpperCase() })}
                            fullWidth
                            required
                            disabled={editMode}
                        />
                        <TextField
                            label="Nombre"
                            value={currentTenant.name}
                            onChange={(e) => setCurrentTenant({ ...currentTenant, name: e.target.value })}
                            fullWidth
                            required
                        />
                        <TextField
                            label="URL Backend"
                            value={currentTenant.urlBackend}
                            onChange={(e) => setCurrentTenant({ ...currentTenant, urlBackend: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Version App"
                            value={currentTenant.codVersion}
                            onChange={(e) => setCurrentTenant({ ...currentTenant, codVersion: e.target.value })}
                            fullWidth
                        />
                        <TextField
                            label="Codigo Build"
                            value={currentTenant.codBuild}
                            onChange={(e) => setCurrentTenant({ ...currentTenant, codBuild: e.target.value })}
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={currentTenant.enabled}
                                    onChange={(e) => setCurrentTenant({ ...currentTenant, enabled: e.target.checked })}
                                />
                            }
                            label="Activo"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" sx={{ backgroundColor: '#3d4d87' }}>
                        {editMode ? 'Guardar Cambios' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Tenants;
